import {
  getWeekendBlockState,
  getWeekendPauseState,
  WEEKEND_PAUSED_GROUPS,
  WEEKEND_TRADING_PAUSE_ENABLED,
  WeekendBlockState,
} from '@/config/weekend-trading';
import { useAccountStore } from '@/store/use-account-store';
import { useMarketStore } from '@/store/use-markets-store';
import { CapitalStatus } from '@/types';
import { useEffect, useState } from 'react';
import { useStore } from 'zustand';

const getAccurateNow = () =>
  new Date(useAccountStore.getState().getAccurateTime());

// Mirrors `getIsStatusFunded` — inlined because `src/hooks` must not import
// from `src/features` (enforced by the import/no-restricted-paths zones).
const FUNDED_STATUSES: CapitalStatus[] = [
  CapitalStatus.PRE_FUNDED,
  CapitalStatus.FUNDED,
  CapitalStatus.FUNDED_BLOCKED,
  CapitalStatus.FUNDED_FAILED_MAX_DAILY_LOSS,
  CapitalStatus.FUNDED_FAILED_MAX_DRAWDOWN,
];

export type WeekendTrading = WeekendBlockState;

/**
 * Weekend restriction for a symbol — the platform-wide pause and the
 * funded-only CME break combined. Re-evaluates exactly when the window flips
 * (and whenever the tab becomes visible again, so a backgrounded throttled
 * timer cannot leave a stale enabled form on screen).
 *
 * See `@/config/weekend-trading` for both schedules and the allowed-symbol list.
 */
export function useWeekendTrading(symbol: string): WeekendTrading {
  const asset = useStore(
    useMarketStore(symbol),
    (state) => state.marketSpec.asset,
  );
  const isFunded = useAccountStore((state) =>
    FUNDED_STATUSES.includes(state.getCapitalStatus(state.address)),
  );
  const timeOffset = useAccountStore((state) => state.timeOffset);

  const [state, setState] = useState(() =>
    getWeekendBlockState({ symbol, asset, isFunded, now: getAccurateNow() }),
  );

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    const sync = () => {
      clearTimeout(timeout);
      const next = getWeekendBlockState({
        symbol,
        asset,
        isFunded,
        now: getAccurateNow(),
      });
      setState(next);
      if (!next.isRestricted) return;
      timeout = setTimeout(
        sync,
        Math.max(
          next.changesAt.getTime() -
            useAccountStore.getState().getAccurateTime(),
          0,
        ) + 100,
      );
    };

    const onVisibilityChange = () => {
      if (!document.hidden) sync();
    };

    sync();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearTimeout(timeout);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [symbol, asset, isFunded, timeOffset]);

  return state;
}

/**
 * Whether order placement must be blocked for `symbol` right now. Covers new
 * positions, closes, reduce-only orders and TP/SL edits alike.
 */
export default function useWeekendTradingBlock(symbol: string): boolean {
  return useWeekendTrading(symbol).isBlocked;
}

/**
 * Re-renders the caller whenever any weekend-pause window flips. For components
 * that span several symbols (open-orders list, cancel-all) and therefore cannot
 * use {@link useWeekendTrading} — pair it with {@link getIsWeekendTradingBlocked}
 * per row.
 */
export function useWeekendPauseTick(): void {
  const timeOffset = useAccountStore((state) => state.timeOffset);
  const [, bump] = useState(0);

  useEffect(() => {
    if (!WEEKEND_TRADING_PAUSE_ENABLED) return;

    let timeout: ReturnType<typeof setTimeout>;

    const sync = () => {
      clearTimeout(timeout);
      const now = getAccurateNow();
      const nextChange = Math.min(
        ...WEEKEND_PAUSED_GROUPS.map((group) =>
          getWeekendPauseState(group, now).changesAt.getTime(),
        ),
      );
      timeout = setTimeout(
        () => {
          bump((n) => n + 1);
          sync();
        },
        Math.max(nextChange - useAccountStore.getState().getAccurateTime(), 0) +
          100,
      );
    };

    const onVisibilityChange = () => {
      if (document.hidden) return;
      bump((n) => n + 1);
      sync();
    };

    sync();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearTimeout(timeout);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [timeOffset]);
}

/**
 * Imperative equivalent of {@link useWeekendTradingBlock} for submit handlers
 * and non-React callbacks (chart order lines).
 */
export function getIsWeekendTradingBlocked(symbol: string): boolean {
  const { asset } = useMarketStore(symbol).getState().marketSpec;
  const { address, getCapitalStatus } = useAccountStore.getState();
  return getWeekendBlockState({
    symbol,
    asset,
    isFunded: FUNDED_STATUSES.includes(getCapitalStatus(address)),
    now: getAccurateNow(),
  }).isBlocked;
}
